import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api, formatBRL, formatDateBR } from "../lib/api";
import { toast } from "sonner";
import { Calendar, AlertTriangle, Clock, MessageSquare, CheckSquare, Square, ListChecks, Users } from "lucide-react";
import SendWhatsAppModal from "../components/SendWhatsAppModal";
import IndicatorListModal from "../components/IndicatorListModal";
import { useAuth } from "../lib/auth";

function Bucket({ title, icon: Icon, kpi, items, color, testid, selected, onToggle, onSelectAll, allSelected, showSelectAll }) {
  return (
    <div className={`fc-card overflow-hidden border-${color}`} data-testid={testid}>
      <div className="p-5 border-b border-[#1f1f1f]">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Icon size={16} className={color}/>
            <h2 className="font-display text-base font-semibold tracking-tight">{title}</h2>
          </div>
          {showSelectAll && items.length > 0 && (
            <button
              onClick={onSelectAll}
              className="text-[10.5px] uppercase tracking-[0.1em] text-[#D4AF37] hover:text-white flex items-center gap-1"
              data-testid={`${testid}-select-all`}
            >
              {allSelected ? <CheckSquare size={12}/> : <Square size={12}/>}
              {allSelected ? "Desmarcar todos" : "Selecionar todos"}
            </button>
          )}
        </div>
        <div className="grid grid-cols-3 gap-3 text-sm mt-3">
          <div>
            <div className="text-[10px] uppercase text-[#737373]">Contratos</div>
            <div className="font-mono-tab font-bold text-lg mt-0.5">{kpi.count}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-[#737373]">Capital</div>
            <div className="font-mono-tab text-base mt-0.5">{formatBRL(kpi.capital)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-[#737373]">Total a receber</div>
            <div className="font-mono-tab text-base mt-0.5 text-[#D4AF37]">{formatBRL(kpi.total)}</div>
          </div>
        </div>
      </div>
      <div className="max-h-[280px] overflow-y-auto">
        {items.length === 0 ? (
          <div className="p-5 text-[#737373] text-sm">Nenhum contrato neste bucket.</div>
        ) : items.map(it => {
          const rowKey = it.installment_id || it.contract_id;
          const rowId = it.contract_id || it.installment_contract_id;
          const isSel = selected.has(rowKey);
          const isInst = it.source === "installment";
          const kindLabel = it.kind_label || (isInst ? "Parcelado" : "Contrato");
          const parcelaInfo = isInst && it.installment_number ? ` · Parc. ${it.installment_number}/${it.installment_total || "—"}` : "";
          return (
            <div key={rowKey} className={`px-5 py-3 border-b border-[#161616] flex items-center gap-3 hover:bg-[#121212] transition-colors ${isSel ? "bg-[#D4AF37]/5" : ""}`}>
              <button
                onClick={() => onToggle(it)}
                className="text-[#737373] hover:text-[#D4AF37] transition-colors flex-shrink-0"
                data-testid={`select-${rowKey}`}
                title={isSel ? "Desmarcar" : "Selecionar"}
              >
                {isSel ? <CheckSquare size={16} className="text-[#D4AF37]"/> : <Square size={16}/>}
              </button>
              <Link to={`/clientes/${it.client_id}`} className="flex-1 flex items-center justify-between gap-3 min-w-0">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium truncate">{it.client_name}</span>
                    <span className={`badge-base text-[9.5px] ${isInst ? "bg-violet-500/15 text-violet-200 border-violet-500/40" : "bg-[#D4AF37]/15 text-[#E8C547] border-[#D4AF37]/40"}`}>
                      {kindLabel}{parcelaInfo}
                    </span>
                  </div>
                  <div className="text-[11px] text-[#737373]">{formatDateBR(it.due_date)} • {it.indicator_name || "—"}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono-tab text-sm text-[#D4AF37]">{formatBRL(it.balance)}</div>
                  <div className="text-[10px] text-[#737373]">saldo</div>
                </div>
              </Link>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function VencimentosPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Map()); // contract_id -> row
  const [waOpen, setWaOpen] = useState(false);
  const [indicatorIds, setIndicatorIds] = useState([]); // Sprint pós-V1: seleção múltipla
  const [indicatorPickerOpen, setIndicatorPickerOpen] = useState(false);
  const [indicators, setIndicators] = useState([]);
  const [listOpen, setListOpen] = useState(false);

  const isAdminGestora = user?.role === "admin" || user?.role === "gestora";

  useEffect(() => {
    api.get("/vencimentos").then(r => setData(r.data)).finally(() => setLoading(false));
    if (isAdminGestora) {
      api.get("/indicators").then(r => {
        const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setIndicators(items.filter(i => i.active !== false));
      }).catch(() => setIndicators([]));
    }
  }, [isAdminGestora]);

  // Filtro por indicador — aplicado nos buckets antes de renderizar.
  // atrasados + hoje ganham lógica de "selecionar todos"; próximos 7/15/30 ficam apenas para leitura.
  const filteredData = useMemo(() => {
    if (!data) return null;
    // Sprint pós-V1: filtro multi-indicador. Vazio = todos.
    if (indicatorIds.length === 0) return data;
    const idSet = new Set(indicatorIds);
    const filt = (arr) => (arr || []).filter(x => idSet.has(x.indicator_id));
    const newBuckets = {
      atrasados: filt(data.buckets.atrasados),
      hoje: filt(data.buckets.hoje),
      "7": filt(data.buckets["7"]),
      "15": filt(data.buckets["15"]),
      "30": filt(data.buckets["30"]),
    };
    const recount = (arr) => ({
      count: arr.length,
      capital: arr.reduce((s, x) => s + Number(x.capital || 0), 0),
      juros: arr.reduce((s, x) => s + Number(x.interest_amount || 0), 0),
      total: arr.reduce((s, x) => s + Number(x.balance || 0), 0),
    });
    return {
      buckets: newBuckets,
      kpi: {
        atrasados: recount(newBuckets.atrasados),
        hoje: recount(newBuckets.hoje),
        "7": recount(newBuckets["7"]),
        "15": recount(newBuckets["15"]),
        "30": recount(newBuckets["30"]),
      },
    };
  }, [data, indicatorIds]);

  const selectedIndicators = useMemo(
    () => indicators.filter(i => indicatorIds.includes(i.id)),
    [indicators, indicatorIds],
  );

  // Sprint 7: chave única por linha (installment tem ID diferente do contract)
  const rowKey = (row) => row.installment_id || row.contract_id;

  const toggle = (row) => {
    setSelected(prev => {
      const next = new Map(prev);
      const k = rowKey(row);
      if (next.has(k)) next.delete(k);
      else next.set(k, row);
      return next;
    });
  };
  const clearAll = () => setSelected(new Map());

  // Select-all helpers
  const buckAtras = filteredData?.buckets.atrasados || [];
  const buckHoje = filteredData?.buckets.hoje || [];
  const allAtrasSelected = buckAtras.length > 0 && buckAtras.every(x => selected.has(rowKey(x)));
  const allHojeSelected = buckHoje.length > 0 && buckHoje.every(x => selected.has(rowKey(x)));
  const bothSelected = allAtrasSelected && allHojeSelected && (buckAtras.length + buckHoje.length > 0);

  const selectAll = (items) => {
    setSelected(prev => {
      const next = new Map(prev);
      const allIn = items.length > 0 && items.every(x => next.has(rowKey(x)));
      if (allIn) items.forEach(x => next.delete(rowKey(x)));
      else items.forEach(x => next.set(rowKey(x), x));
      return next;
    });
  };

  const selectBoth = () => selectAll([...buckAtras, ...buckHoje]);

  const selectedList = Array.from(selected.values());
  const selectedTotal = selectedList.reduce((a, r) => a + Number(r.balance || 0), 0);

  // Para o modal de lista: só itens dos buckets atrasados+hoje do indicador selecionado
  const listItems = useMemo(() => {
    const inBucket = new Map();
    (buckAtras || []).forEach(x => inBucket.set(rowKey(x), "atrasados"));
    (buckHoje || []).forEach(x => inBucket.set(rowKey(x), "hoje"));
    return selectedList
      .filter(r => inBucket.has(rowKey(r)))
      .map(r => ({
        client_id: r.client_id,
        client_name: r.client_name,
        indicator_id: r.indicator_id,
        indicator_name: r.indicator_name,
        due_date: r.due_date,
        kind: r.kind || "contract",
        kind_label: r.kind_label || "Contrato",
        installment_number: r.installment_number,
        installment_total: r.installment_total,
        bucket: inBucket.get(rowKey(r)),
      }))
      .sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""));
  }, [selectedList, buckAtras, buckHoje]);

  const openGenerateList = () => {
    // Sprint pós-V1: multi-indicador — se nenhum for selecionado, considera "todos".
    if (listItems.length === 0) { toast.error("Selecione ao menos um cliente atrasado ou que vence hoje"); return; }
    setListOpen(true);
  };

  return (
    <Layout>
      <div className="mb-5">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Operação</div>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Vencimentos</h1>
        <p className="text-[#A3A3A3] text-sm mt-1">Distribuição automática por janelas de vencimento — selecione para futura ação em lote</p>
      </div>

      {/* Filtro por indicador (multi-select — Sprint pós-V1) + toolbar de seleção */}
      {isAdminGestora && (
        <div className="fc-card p-4 mb-5" data-testid="indicator-filter-bar">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="flex-1 min-w-0 relative">
              <label className="text-[10.5px] uppercase tracking-[0.14em] text-[#737373] flex items-center gap-1.5 mb-1">
                <Users size={11}/> Filtrar por indicador (múltiplos)
              </label>
              <button
                type="button"
                onClick={() => setIndicatorPickerOpen(v => !v)}
                className="fc-input text-left w-full flex items-center justify-between gap-2"
                data-testid="indicator-filter-toggle"
              >
                <span className="truncate">
                  {indicatorIds.length === 0
                    ? "Todos os indicadores"
                    : indicatorIds.length === 1
                      ? (indicators.find(i => i.id === indicatorIds[0])?.name || "1 selecionado")
                      : `${indicatorIds.length} indicadores selecionados`}
                </span>
                <span className="text-[#737373] text-[10px]">{indicatorPickerOpen ? "▲" : "▼"}</span>
              </button>
              {indicatorPickerOpen && (
                <div
                  className="absolute z-30 mt-1 w-full max-h-72 overflow-y-auto rounded-md border border-[#262626] bg-[#0a0a0a] shadow-2xl"
                  data-testid="indicator-picker"
                >
                  <button
                    type="button"
                    onClick={() => setIndicatorIds([])}
                    className={`w-full text-left px-3 py-2 text-[13px] border-b border-[#1a1a1a] hover:bg-[#161616] flex items-center gap-2 ${indicatorIds.length === 0 ? "text-[#D4AF37]" : "text-[#A3A3A3]"}`}
                    data-testid="indicator-picker-all"
                  >
                    {indicatorIds.length === 0 ? <CheckSquare size={13}/> : <Square size={13}/>}
                    Todos os indicadores
                  </button>
                  {indicators.map(i => {
                    const checked = indicatorIds.includes(i.id);
                    return (
                      <button
                        key={i.id}
                        type="button"
                        onClick={() => setIndicatorIds(prev => (
                          prev.includes(i.id) ? prev.filter(x => x !== i.id) : [...prev, i.id]
                        ))}
                        className={`w-full text-left px-3 py-2 text-[13px] hover:bg-[#161616] flex items-center gap-2 ${checked ? "text-[#D4AF37]" : "text-[#A3A3A3]"}`}
                        data-testid={`indicator-picker-item-${i.id}`}
                      >
                        {checked ? <CheckSquare size={13}/> : <Square size={13}/>}
                        {i.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <button
              onClick={selectBoth}
              className="fc-btn-ghost text-[12px] flex items-center gap-1.5"
              data-testid="select-both-buckets"
              title="Selecionar todos os atrasados + todos que vencem hoje"
            >
              {bothSelected ? <CheckSquare size={12}/> : <Square size={12}/>}
              Marcar atrasados + hoje
            </button>
            <button
              onClick={openGenerateList}
              className="fc-btn-gold text-[13px] flex items-center gap-1.5"
              data-testid="generate-indicator-list"
            >
              <ListChecks size={13}/> Gerar lista consolidada
            </button>
          </div>
        </div>
      )}

      {/* Barra de ação em lote — visível quando há seleção */}
      {selectedList.length > 0 && (
        <div className="sticky top-0 z-20 fc-card border-[#D4AF37]/40 bg-[#141414] px-4 py-3 mb-5 flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between" data-testid="bulk-action-bar">
          <div className="text-[13px]">
            <strong className="text-[#D4AF37]" data-testid="selected-count">{selectedList.length}</strong>
            <span className="text-[#A3A3A3] ml-1.5">contrato(s) selecionado(s)</span>
            <span className="text-[#737373] ml-3">•</span>
            <span className="text-[#A3A3A3] ml-3">Total: <span className="font-mono-tab text-[#D4AF37]">{formatBRL(selectedTotal)}</span></span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setWaOpen(true)}
              className="fc-btn-gold flex items-center gap-1.5 text-xs"
              data-testid="bulk-whatsapp"
              title="Enviar cobrança via WhatsApp para todos os selecionados"
            >
              <MessageSquare size={12}/> Enviar Cobrança
            </button>
            <button onClick={clearAll} className="fc-btn-ghost text-xs" data-testid="bulk-clear">
              Limpar seleção
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="grid lg:grid-cols-2 gap-5">
          {[1,2,3,4,5].map(i=>(<div key={i} className="fc-card h-64 shimmer"/>))}
        </div>
      ) : filteredData && (
        <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-5">
          <Bucket title="Atrasados" icon={AlertTriangle} color="text-red-300" kpi={filteredData.kpi.atrasados} items={filteredData.buckets.atrasados} testid="bucket-atrasados" selected={selected} onToggle={toggle} showSelectAll={true} allSelected={allAtrasSelected} onSelectAll={() => selectAll(buckAtras)}/>
          <Bucket title="Vence hoje" icon={Clock} color="text-yellow-300" kpi={filteredData.kpi.hoje} items={filteredData.buckets.hoje} testid="bucket-hoje" selected={selected} onToggle={toggle} showSelectAll={true} allSelected={allHojeSelected} onSelectAll={() => selectAll(buckHoje)}/>
          <Bucket title="Próximos 7 dias" icon={Calendar} color="text-[#D4AF37]" kpi={filteredData.kpi["7"]} items={filteredData.buckets["7"]} testid="bucket-7" selected={selected} onToggle={toggle}/>
          <Bucket title="Próximos 15 dias" icon={Calendar} color="text-blue-300" kpi={filteredData.kpi["15"]} items={filteredData.buckets["15"]} testid="bucket-15" selected={selected} onToggle={toggle}/>
          <Bucket title="Próximos 30 dias" icon={Calendar} color="text-emerald-300" kpi={filteredData.kpi["30"]} items={filteredData.buckets["30"]} testid="bucket-30" selected={selected} onToggle={toggle}/>
        </div>
      )}

      <SendWhatsAppModal
        open={waOpen}
        onClose={() => setWaOpen(false)}
        clients={selectedList.map(r => ({ id: r.client_id, full_name: r.client_name }))}
        defaultCategory="cobranca"
        onSent={clearAll}
      />

      {listOpen && (
        <IndicatorListModal
          indicators={selectedIndicators.length > 0 ? selectedIndicators : indicators}
          items={listItems}
          onClose={() => setListOpen(false)}
        />
      )}
    </Layout>
  );
}
